import { fetchWithTimeout, UPLOAD_TIMEOUT_MS } from './fetchWithTimeout';
import type { Route } from '../domain/types';
import { getToken, clearToken, saveRoutesSnapshot, getRoutesSnapshot } from '../offline/db';

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

/** The build this phone is running — the `_IMAGE` tag baked in at build time. */
const APP_VERSION: string = (() => {
  const raw = (import.meta as unknown as { env?: Record<string, string> })
    .env?.VITE_APP_VERSION ?? 'dev';
  const tag = (raw.split(':').pop() ?? raw).slice(0, 30);
  /*
   * ...and WHERE it is running. A tab and an installed app are the same code
   * with very different survival odds: the OS evicts browser tabs first when
   * music or a game wants memory, which is what the 'died' reports were. This
   * rides along on a header we already send, so the office can see who is in a
   * tab without asking anybody or adding a single row anywhere.
   */
  let where = 'tab';
  try {
    if (window.matchMedia('(display-mode: standalone)').matches
        || (window.navigator as unknown as { standalone?: boolean }).standalone === true) {
      where = 'home';
    }
  } catch { /* leave it as tab */ }
  return `${tag}+${where}`;
})();

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetchWithTimeout(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      /*
       * So the office can tell which build each phone is on without ringing
       * anybody. Only /me/routes/today records it, but sending it everywhere
       * costs nothing and means any future endpoint can use it.
       *
       * Must also be listed in the API's CORS allowHeaders — a custom header
       * the server has not agreed to makes the browser refuse the whole
       * request, which would take the route screen down for everyone.
       */
      'X-App-Version': APP_VERSION,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  if (res.status === 401) {
    await clearToken();
    /*
     * Not while already there. Held error reports are flushed with no token on
     * the login screen, and each 401 would assign('/login') again — a reload,
     * another flush, another 401. Sitting still is the correct behavior once
     * the driver is already being asked to sign in.
     */
    // Nor from the robot's /engine-check page (no sign-in by design; 15 Oct).
    if (!/^\/(login|engine-check)/.test(window.location.pathname)) {
      window.location.assign('/login');
    }
    throw new ApiError(401, 'unauthorized');
  }
  const json = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(res.status, json.error ?? `http_${res.status}`);
  return json;
}

/** Was this saved today, on this phone's clock? See getTodayRoutes. */
function isToday(at: number | undefined): boolean {
  if (!at) return false;   // saved before this check existed: refetch instead
  const then = new Date(at);
  const now = new Date();
  return then.getFullYear() === now.getFullYear()
      && then.getMonth() === now.getMonth()
      && then.getDate() === now.getDate();
}

export const api = {
  /** Has this driver accepted the current privacy notice? */
  /*
   * Cover requests. Not queued offline like a completion: a request for next
   * Tuesday is not time-critical, and a silent queue would leave a driver
   * believing they had asked when nothing had been sent.
   */
  requestCoverage: (body: {
    from: string; to: string; kind: 'time_off' | 'backup';
    reasonCode: string; note?: string | null; backupName?: string | null;
  }) => request<{ ok: boolean; error?: string; call_office?: boolean; route_start?: string }>('/coverage', {
    method: 'POST', body: JSON.stringify(body),
  }),

  listCoverageRequests: () => request<{
    requests: Array<{
      id: string; from_date: string; to_date: string; kind: string;
      reason: string; status: string; decision_note: string | null;
    }>;
  }>('/coverage'),

  /**
   * Every route today, with every stop. Replaces the single-route call, which
   * hid a driver's second route until the first was finished.
   *
   * Falls back to the saved snapshot exactly as the old one did, so a driver
   * who opens the app inside a building still sees their day.
   */
  getTodayRoutes: async (): Promise<{ routes: Route[]; stale?: boolean }> => {
    try {
      const fresh = await request<{ routes: Route[] }>('/me/routes/today');
      /*
       * A 200 that is not our answer. request() turns an unreadable body into
       * {}, so a hospital wifi sign-in page or a reply cut off on one bar
       * arrived here as "no routes" -- and crashed the screen on .map (Manjit,
       * 2 Nov 5:45 pm) after overwriting the good snapshot with nothing. Treat
       * it as the network failure it is, so the saved copy below is used.
       */
      if (!Array.isArray(fresh?.routes) || fresh.routes.some((r) => !Array.isArray(r?.tasks))) {
        throw new Error('bad_routes_reply');
      }
      await saveRoutesSnapshot(fresh.routes);
      return { routes: fresh.routes };
    } catch (err) {
      /*
       * A real answer from the server is never papered over with a snapshot.
       * The old single-route call had this rule and the replacement lost it,
       * so an expired session showed yesterday's work forever instead of the
       * login screen. request() already redirects on 401; re-throwing anything
       * the server actually said keeps that true.
       */
      if (err instanceof ApiError) throw err;
      /*
       * And the snapshot is only good for the day it was taken. Without this a
       * driver opening the app in a basement could be shown YESTERDAY's stops
       * as today's work, labelled merely "saved earlier". Refusing it means
       * they see "cannot reach the office", which is the truth.
       */
      const snap = await getRoutesSnapshot();
      const saved = snap?.routes;
      if (Array.isArray(saved) && saved.length > 0 && isToday(snap?.savedAt)) {
        return { routes: saved as Route[], stale: true };
      }
      throw err;
    }
  },

  /**
   * What this phone already knows, with no network call at all.
   *
   * Used to paint the screen instantly while the real request runs behind it.
   * Returns null when there is nothing from today, so a caller can tell
   * "nothing cached" from "cached and empty".
   */
  cachedRoutesToday: async (): Promise<Route[] | null> => {
    const snap = await getRoutesSnapshot();
    if (!snap || !Array.isArray(snap.routes) || !isToday(snap.savedAt)) return null;
    return snap.routes as Route[];
  },

  /** Is this stop already finished at the office? (10 Oct) Answers in 4 s or not at all. */
  taskState: (taskId: string) => Promise.race([
    request<{ status: string; completedAt?: string | null }>(`/tasks/${taskId}/state`),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 4_000)),
  ]),

  /** One tap: take the route, take the stop, mark arrival. */
  beginStop: (taskId: string, lat: number | null, lng: number | null) =>
    request<{ ok: boolean; distance_m?: number; radius_m?: number; too_far?: boolean }>(
      `/me/stops/${taskId}/begin`,
      { method: 'POST', body: JSON.stringify({ lat, lng }) }),

  /**
   * The driver has opened this stop.
   *
   * The missing middle point between arriving at a facility and completing the
   * stop. Without it, a stop that took forty minutes is indistinguishable from
   * one where the driver arrived, waited thirty-eight minutes at a pharmacy
   * window, and then took two minutes in the app.
   *
   * Never awaited, never queued offline, never shown to the driver. A lost
   * measurement is a lost measurement; it must not cost anybody a second.
   */
  stopOpened: (taskId: string, lat: number | null, lng: number | null,
               accuracyM: number | null) =>
    request<{ ok: boolean }>(`/tasks/${taskId}/opened`, {
      method: 'POST', body: JSON.stringify({ lat, lng, accuracyM }),
    }),

  /** Diagnostics. Never awaited by anything that matters. */
  reportError: (body: {
    kind: string; screen: string | null; detail: string | null;
    userAgent: string; appVersion: string; online: boolean; occurredAt: string;
  }) => request<{ ok: boolean }>('/me/errors', {
    method: 'POST', body: JSON.stringify(body),
  }),

  privacyAccepted: (version: string) =>
    request<{ accepted: boolean }>(`/me/privacy?version=${encodeURIComponent(version)}`),

  acknowledgePrivacy: (version: string) =>
    request<{ ok: true }>('/me/privacy', {
      method: 'POST', body: JSON.stringify({ version }),
    }),

  /** Is an identity check due right now? */
  identityDue: (taskId?: string | null) =>
    request<{ due: boolean; trigger?: 'route_start' | 'random_stop'; live?: boolean }>(
      `/identity/due${taskId ? `?taskId=${taskId}` : ''}`),

  // Messages from the office (22 Oct).
  officeMessages: () =>
    request<{ messages: Array<{ id: number; body: string; route_change: boolean; created_at: string }> }>('/messages'),
  readOfficeMessage: (id: number) =>
    request<{ ok: boolean }>(`/messages/${id}/read`, { method: 'POST', body: '{}' }),

  // Before a skip (17 Oct): is a scan needed first?
  identityDueSkip: (taskId: string) =>
    request<{ due: boolean; trigger?: 'route_start' | 'random_stop' }>(
      `/identity/due-skip?taskId=${taskId}`),

  // The scan cannot be taken: ask the office to unlock the stop, and ask again
  // until it has (17 Oct).
  identityUnlockRequest: (taskId: string, reason: 'camera_failed' | 'no_face' | 'scan_not_saved') =>
    request<{ unlocked: boolean }>('/identity/unlock-request', {
      method: 'POST', body: JSON.stringify({ taskId, reason }),
    }),

  signIdentity: (contentType: string) =>
    request<{ uploadUrl: string; objectPath: string;
              requiredHeaders?: Record<string, string> }>('/identity/sign', {
      method: 'POST', body: JSON.stringify({ contentType }),
    }),

  /** Does this driver still owe us a baseline photo? */
  faceReferenceNeeded: () =>
    request<{ needed: boolean }>('/identity/reference/needed'),

  setFaceReference: (objectPath: string) =>
    request<{ ok: boolean }>('/identity/reference', {
      method: 'POST', body: JSON.stringify({ objectPath }),
    }),

  submitIdentity: (body: {
    objectPath: string;
    trigger: 'route_start' | 'random_stop' | 'manual';
    taskId: string | null;
  }) => request<{ ok: boolean; flagged: boolean; result: string; code?: string | null }>('/identity/check', {
    method: 'POST', body: JSON.stringify(body),
  }),

  /*
   * accept, start, advance and switch-request are gone from the client.
   * begin_stop does the first three in one call, and cover requests replaced
   * the switch. The SERVER endpoints stay: a phone that has not taken the
   * update yet still calls them, and nothing is gained by breaking it.
   */

  /*
   * Completion, skip and proof upload are NOT here. They live in flush.ts,
   * which goes through the offline queue so a submission survives no signal and
   * a closed app. There were duplicates of all three in this file, unreachable
   * since the rebuild; they are gone, because two implementations of the most
   * important operation in the app is an invitation to fix the wrong one.
   */
};

/** Raw PUT to a signed GCS URL (no auth header — the signature IS the auth). */
export async function uploadToSignedUrl(
  url: string, blob: Blob, requiredHeaders?: Record<string, string>,
): Promise<void> {
  // Must match the headers the URL was signed with -- see SignedUpload in the
  // API. Omitting one yields 403 SignatureDoesNotMatch, not a network error.
  const res = await fetchWithTimeout(url, {
    method: 'PUT',
    headers: requiredHeaders ?? { 'Content-Type': blob.type },
    body: blob,
  }, UPLOAD_TIMEOUT_MS);
  if (!res.ok) throw new ApiError(res.status, 'upload_failed');
}
